import {
  API_BASE_URL,
  apiClient,
  createApiClient,
  getHeader,
  getOrganizationIdParamString,
  handleError,
  handleResponse
} from '@/helpers/api'
import store from '@/store'
import router from '@/router/router'

const apiAccountClient = createApiClient(`${API_BASE_URL}/Account`, false)
const apiCmsClient = createApiClient(`${API_BASE_URL}/content`)

// EXPORTS
export const userService = {
  // ACCOUNT CLIENT
  register,
  registerIvoris,
  registrationConfirmation,
  login,
  refreshToken,
  logout,
  changePassword,
  accountConfirmationVerification,
  sendConfirmationEmail,
  sendPasswordResetEmail,
  resetPassword,
  keycloakLogin,
  keycloakRegister,
  sendChangeEmailMail,
  confirmEmailChange,
  // API CLIENT
  saveUserProfile,
  getUserProfile,
  saveUserSettings,
  saveUserSettingsAsAdmin,
  getUserSettings,
  createCreditOrder,
  addCredit,
  activateVoucher,
  getCreditBalance,
  deleteUserAccount,
  lockUserSettings,
  setOrgaMerge
}

// API CALLS
// ACCOUNT CLIENT
async function register(payload) {
  return apiAccountClient
    .post('/register', payload)
    .then(handleResponse)
    .catch(handleRegistrationError)
}

async function registerIvoris(payload) {
  return apiAccountClient
    .post('/register/ivoris', payload)
    .then(handleResponse)
    .catch(handleError)
}

async function registrationConfirmation(payload) {
  return apiAccountClient
    .post(`/confirm?token=${payload.token}&userEmail=${payload.userEmail}`)
    .then(handleResponse)
    .catch(handleError)
}

async function login(payload) {
  return apiAccountClient
    .post('/login', payload)
    .then(handleResponse)
    .catch(handleError)
}

async function refreshToken() {
  return apiAccountClient
    .get('/refreshToken', getHeader())
    .then(response => {
      store.commit('SET_TOKEN', response.data.jwt)
      store.commit('SET_PERMISSIONS', response.data.jwt, { root: true })
      return response.data
    })
    .catch(handleError)
}

async function logout() {
  const userEmail = store.getters.username
  if (!userEmail) {
    console.error('No username or email found for logout.')
    return
  }
  try {
    await store.dispatch('resetApp', true, { root: true })
    // @TODO: Why do we fire logout?
    return await apiAccountClient.post(
      '/logout',
      JSON.stringify(userEmail),
      getHeader()
    )
  } catch (error) {
    console.error('Logout failed:', error)
    throw error
  }
}

function changePassword(payload) {
  return apiAccountClient
    .post('/change', payload, getHeader())
    .then(handleResponse)
    .catch(handleError)
}

async function accountConfirmationVerification(payload) {
  return apiAccountClient
    .post('/verify', { userEmail: payload.userEmail })
    .then(handleResponse)
    .catch(handleError)
}

async function sendConfirmationEmail(payload) {
  return apiAccountClient
    .post('/confirmationEmail', payload, getHeader())
    .then(handleResponse)
    .catch(handleError)
}

async function sendPasswordResetEmail(payload) {
  return apiAccountClient
    .post('/passwordResetEmail', payload)
    .then(handleResponse)
    .catch(handleError)
}

async function resetPassword(payload) {
  return apiAccountClient
    .post('/passwordReset', payload)
    .then(handleResponse)
    .catch(handleError)
}

async function keycloakLogin(payload) {
  return apiAccountClient
    .post('/keycloak-login', null, {
      headers: { Authorization: 'bearer ' + payload.token }
    })
    .then(handleResponse)
    .catch(handleError)
}

async function keycloakRegister(payload) {
  return apiAccountClient
    .post('/keycloak-register', null, {
      headers: { Authorization: 'bearer ' + payload.token }
    })
    .then(handleResponse)
    .catch(handleError)
}

async function sendChangeEmailMail(payload) {
  return apiAccountClient
    .post('/changeEmail', payload, getHeader())
    .then(handleResponse)
    .catch(handleError)
}

async function confirmEmailChange(payload) {
  return apiAccountClient
    .get(
      `/confirmEmailChange?token=${payload.token}&newEmail=${payload.newEmail}&userId=${payload.userId}`,
      getHeader()
    )
    .then(handleResponse)
    .catch(handleError)
}

// API CLIENT
async function saveUserProfile(payload) {
  return apiClient
    .post(`/UserProfile${getOrganizationIdParamString()}`, payload)
    .then(handleResponse)
    .catch(handleError)
}

async function getUserProfile() {
  return apiClient
    .get(`/UserProfile${getOrganizationIdParamString()}`)
    .then(response => {
      if (response.data.customerNumber === null || undefined || '') {
        setTimeout(() => {
          router.push({ name: 'profile', params: { locale: 'de' } })
        }, 300)
      }
      return response.data
    })
    .catch(handleError)
}

async function saveUserSettings(payload) {
  return apiClient
    .post('/UserSettings', payload)
    .then(handleResponse)
    .catch(handleError)
}

async function saveUserSettingsAsAdmin(payload) {
  return apiClient
    .post(`/organization/${payload.organizationId}/user/settings`, payload)
    .then(handleResponse)
    .catch(handleError)
}

async function getUserSettings(payload) {
  const userId = payload ? '?userId=' + payload.userId : ''
  return apiClient
    .get(`/UserSettings${userId}`)
    .then(handleResponse)
    .catch(handleError)
}

async function createCreditOrder(payload) {
  return apiClient
    .post('/creditaccount/create-order', payload)
    .then(handleResponse)
    .catch(handleError)
}

async function addCredit(payload) {
  return apiClient
    .post('/creditaccount/replenish', payload)
    .then(handleResponse)
    .catch(handleError)
}

async function activateVoucher(payload) {
  return apiClient
    .post('/voucher/activate', payload)
    .then(handleResponse)
    .catch(handleError)
}

async function getCreditBalance() {
  return apiClient
    .get('/creditaccount')
    .then(handleResponse)
    .catch(handleError)
}

async function deleteUserAccount(payload) {
  return apiClient
    .delete('/account', getHeader({ data: payload }))
    .then(handleResponse)
}

async function lockUserSettings(payload) {
  return apiClient
    .put(
      '/UserSettings/lockUserSettings/' + payload.id,
      JSON.stringify(payload.locked),
      getHeader()
    )
    .then(handleResponse)
    .catch(handleError)
}

async function setOrgaMerge(payload) {
  return apiClient
    .put(
      '/UserSettings/setOrgaMerge/' + payload.id,
      JSON.stringify(payload.enabled),
      getHeader()
    )
    .then(handleResponse)
    .catch(handleError)
}

// HELPERS
async function handleRegistrationError(inputError) {
  if (inputError instanceof Error && inputError.message === 'Network Error') {
    inputError.fromCms = false
    inputError.response = { status: '503' }
    return Promise.reject(inputError)
  }
  const statuscode = inputError.response.status
  return apiCmsClient
    .get('/registrationerrors', {
      params: {
        statuscode: statuscode
      }
    })
    .then(response => {
      let errorReturned = response.data[0]
      errorReturned.fromCms = true
      return Promise.reject(errorReturned)
    })
    .catch(() => {
      inputError.fromCms = false
      return Promise.reject(inputError)
    })
}
